import React from "react";
import { Drawer, Box, IconButton } from "@mui/material";
import CancelIcon from '@mui/icons-material/Cancel';

interface DrawerWrapperProps {
  open: boolean;
  onClose: () => void;
  header?: React.ReactNode;
  body?: React.ReactNode;
  footer?: React.ReactNode;
  styles?: React.CSSProperties;
  bodySx?: any;
}

const hasHeaderContent = (node: React.ReactNode): boolean => {
  if (!node) return false;
  if (React.isValidElement(node) && node.type === React.Fragment) {
    const children = (node.props as any)?.children;
    if (!children) return false;
    if (Array.isArray(children)) return children.length > 0;
    return true;
  }
  return true;
};

const DrawerWrapper: React.FC<DrawerWrapperProps> = ({
  open,
  onClose,
  header,
  body,
  footer,
  styles,
  bodySx,
}) => {
  const showHeader = hasHeaderContent(header);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          // default width, can be overridden by `styles` prop (e.g. "50%")
          width: 350,
          maxWidth: '100%',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          ...styles,
        },
      }}
      sx={{ overflow: 'hidden' }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
        {showHeader ? (
          <Box
            component="header"
            sx={{
              position: 'relative',
              width: '100%',
              px: 2,
              py: 1,
              boxSizing: 'border-box',
              minHeight: 56,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
          >
            <Box sx={{ flex: 1, fontSize: 18, fontWeight: 600 }}>{header}</Box>
            <IconButton
              onClick={onClose}
              sx={{ position: 'absolute', right: 8, top: 8, color: 'gray' }}
              aria-label="close drawer"
            >
              <CancelIcon />
            </IconButton>
          </Box>
        ) : (
          <IconButton
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'gray', zIndex: 10 }}
            aria-label="close drawer"
          >
            <CancelIcon />
          </IconButton>
        )}

        <Box component="main" sx={{ flex: 1, overflow: 'auto', width: '100%', ...bodySx }}>
          {body}
        </Box>

        {footer && (
          <Box component="footer" sx={{ px: 2, py: 1 }}>
            {footer}
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default DrawerWrapper;
